// libraries
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
// constants
import { USER_ROLES } from '../../constants/constants';
// Components
import { withRouter } from '../shared/WithRouter';
// actions
import { BlockRouteTransitions, UnBlockRouteTransitions } from '../../actions/router';
import { PatientTimer } from '../../actions/patient';
// services
import { getTimeTrackingGracePeriod } from '../../services/administration';
import { getPatientId } from '../../services/helpers';
import { createTimer, endTimer } from '../../services/patient';

const INIT_TIME_FORMAT = '00:00';
export const MAX_MINUTE_TIME = 200;
const MAX_TIME = `${MAX_MINUTE_TIME}:00`;
const ELEMENT_NAME_TO_BLOCK = 'isTimer';

const numberFormat = (number, length = 2) => (`0${number}`).slice(-(length));

const getFormattedTime = (time) => {
  const seconds = Math.floor(time / 1000) % 60;
  const minutes = Math.floor(time / 60000) % 999;
  const minutesLength = minutes > 99 ? 3 : 2;

  return minutes >= MAX_MINUTE_TIME ? MAX_TIME : `${numberFormat(minutes, minutesLength)}:${numberFormat(seconds)}`;
};

export class Stopwatch extends Component {
  state = {
    isTimerOn: true,
    savedTime: 0,
    formattedTime: INIT_TIME_FORMAT,
    isFullShown: false,
    timerGracePeriod: 0,
    timerIdState: null,
  };

  timer = null;

  componentDidMount() {
    // save data if a user reloads a page
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Get the timer grace period
    this.loadTimeTrackingGracePeriod();
  }

  componentDidUpdate(prevProps) {
    const {
      patient, patientTimer, userRole, blockRouteTransitions, isBlocked,
    } = this.props;
    const { savedTime, isTimerOn, timerGracePeriod } = this.state;
    const isCnUser = (userRole === USER_ROLES.CN);
    const isPesUser = (userRole === USER_ROLES.PES);
    let timerId;

    // Reset the timer if we're changing patients
    const patientId = getPatientId(patient);
    if (patientId !== getPatientId(prevProps.patient)) {
      this.resetTimer();
      // Start the timer automatically when opening a patient chart
      if (patientId && (isCnUser || isPesUser)) {
        const storedTimerId = localStorage.getItem('timerId');
        if (storedTimerId) {
          timerId = parseInt(storedTimerId, 10);
          const timerTime = parseInt(localStorage.getItem('timerTime'), 10);
          // Use the setState callback to ensure savedTime is updated
          this.setState({ savedTime: timerTime, timerIdState: timerId }, () => {
            // This callback runs *after* savedTime has been updated in state
            localStorage.removeItem('pId');
            localStorage.removeItem('timerId');
            localStorage.removeItem('timerTime');
            // Call startTimer *after* state is guaranteed to be updated
            this.startTimer();
          });
        } else {
          // If no timerId in localStorage, proceed to startTimer directly
          this.startTimer();
        }
      }
    }

    // Update the timer data for the patient
    const formattedTime = getFormattedTime(savedTime);
    const timer = patient?.timer;
    if (timer?.time !== formattedTime) {
      if (this.state.timerIdState) {
        patientTimer({ time: formattedTime, id: this.state.timerIdState });
      } else {
        patientTimer({ time: formattedTime });
      }
    }

    // Start or stop timer based on shared state
    const sharedIsTimerOn = timer?.isTimerOn;
    if (patientId !== undefined
        && sharedIsTimerOn !== undefined
        && sharedIsTimerOn !== isTimerOn) {
      if (sharedIsTimerOn && (isCnUser || isPesUser)) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    }

    // Check if we need to reset the timer
    if (timer?.reset) {
      this.resetTimer();
    }

    // Check if we need to block transitions due to grace period
    if (!timer?.reset && savedTime > timerGracePeriod && !isBlocked) {
      // Notify that there is timer data to notify user about
      blockRouteTransitions(ELEMENT_NAME_TO_BLOCK);

      // Notify backend of timer start
      if (!timer?.id && !this.state.timerIdState) {
        const createTimerRequest = createTimer(
          patientId,
          {
            duration: this.getMinutesFromTimer(savedTime),
          },
        );
        const createTimerPromise = createTimerRequest.promise;
        createTimerPromise.then((result) => {
          delete createTimerRequest.promise;
          patientTimer({ id: result.id });
        });
      }
    }
  }

  componentWillUnmount() {
    const { unBlockRouteTransitions } = this.props;
    unBlockRouteTransitions(ELEMENT_NAME_TO_BLOCK);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  getMinutesFromTimer(time) {
    return Math.round((time / 1000) / 60);
  }

  handleBeforeUnload = (event) => {
    const { savedTime, timerGracePeriod } = this.state;
    if (savedTime > 0 && (timerGracePeriod === 0 || savedTime > timerGracePeriod)) {
      // Prevent default to close
      if (event) {
        event.preventDefault();
        // For legacy browsers
        // eslint-disable-next-line no-param-reassign
        event.returnValue = true;
      }
    }
  };

  startTimer = () => {
    const { patientTimer } = this.props;
    this.setState(state => ({
      isTimerOn: true,
      savedTime: state.savedTime,
      timerStart: moment().diff(state.savedTime),
      isFullShown: true,
    }));
    patientTimer({ isTimerOn: true, id: this.state.timerIdState });

    this.setTimer();
  };

  stopTimer = () => {
    const { patient } = this.props;
    const { savedTime } = this.state;
    clearInterval(this.timer);
    this.setState({ isTimerOn: false, timerIdState: null });

    // Notify backend of timer stop
    if (patient?.timer?.id) {
      endTimer(
        getPatientId(patient),
        patient.timer.id,
        {
          duration: this.getMinutesFromTimer(savedTime),
        },
      );
    }
  };

  resetTimer = () => {
    const { unBlockRouteTransitions, patientTimer } = this.props;
    clearInterval(this.timer);
    patientTimer({ reset: false, id: null });
    this.setState({
      isTimerOn: false,
      savedTime: 0,
      formattedTime: INIT_TIME_FORMAT,
      isFullShown: false,
      timerIdState: null,
    });
    unBlockRouteTransitions(ELEMENT_NAME_TO_BLOCK);
  };

  setTimer = () => {
    const { patientTimer } = this.props;
    this.timer = setInterval(() => {
      const savedTime = moment().diff(this.state.timerStart);
      const formattedTime = getFormattedTime(savedTime);

      this.setState({
        savedTime,
        formattedTime,
      }, (formattedTime === MAX_TIME) ? () => patientTimer({ isTimerOn: false }) : null);
    }, 500);
  };

  loadTimeTrackingGracePeriod() {
    const getGracePeriodRequest = getTimeTrackingGracePeriod();
    const getGracePeriodPromise = getGracePeriodRequest.promise;

    getGracePeriodPromise.then((data) => {
      if (data) {
        const timerGracePeriod = data?.value ? data?.value * 1000 : 0;
        this.setState({
          timerGracePeriod,
        });
      }
    });
  }

  render() {
    const { userRole, patient } = this.props;
    const isCnUser = (userRole === USER_ROLES.CN);
    const isPesUser = (userRole === USER_ROLES.PES);
    const { isFullShown, formattedTime, isTimerOn } = this.state;
    let renderBlock = '';

    if (isFullShown) {
      renderBlock = (
        <div className="stopwatch-timer">
          <span className="mr-2">
            {formattedTime}
          </span>
          <i className="bi bi-stopwatch" style={isTimerOn ? { color: 'green' } : { color: 'red' }} />
        </div>
      );
    }
    // Only display timer when on a patient and if a user is a CN or PES
    if (getPatientId(patient) && (isCnUser || isPesUser)) {
      return (
        <Fragment>
          {renderBlock}
        </Fragment>);
    }
    return (<></>);
  }
}

function mapStateToProps(state) {
  return {
    patient: state.patient,
    isBlocked: state.router.blocked,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    blockRouteTransitions: elem => dispatch(BlockRouteTransitions(elem)),
    unBlockRouteTransitions: elem => dispatch(UnBlockRouteTransitions(elem)),
    patientTimer: elem => dispatch(PatientTimer(elem)),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Stopwatch));
